import React, { useState } from 'react';
import { DeviceLogEntry } from './deviceLogs';
import Button from '../ui/Button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../pilot-ui/collapsible';
import { NEUTRAL_CLASS, RESULT_CLASS } from './resultTone';

interface Props {
  entries: DeviceLogEntry[];
  /** Lower-cased search needle; when it matches an entry's ctx, that ctx block auto-expands. */
  searchNeedle?: string;
}

const LEVEL_CLASS: Record<string, string> = {
  debug: 'border border-status-neutral-border bg-status-neutral text-status-neutral-foreground',
  info: 'border border-status-info-border bg-status-info text-status-info-foreground',
  warn: 'border border-status-warning-border bg-status-warning text-status-warning-foreground',
  error: 'border border-status-error-border bg-status-error text-status-error-foreground',
};

/** Plan 104 taxonomy keys surfaced as chips. Older entries without them render no chips. */
const CHIP_KEYS = ['event', 'phase', 'result', 'opcode', 'status', 'credentialSource'] as const;

function chipClass(key: string, value: string): string {
  if (key === 'result') return RESULT_CLASS[value] ?? NEUTRAL_CLASS;
  if (key === 'event') return 'border border-status-info-border bg-status-info text-status-info-foreground';
  return NEUTRAL_CLASS;
}

export function TaxonomyChips({ ctx }: { ctx: Record<string, unknown> | null }) {
  if (!ctx) return null;
  const chips: Array<[string, string]> = [];
  for (const key of CHIP_KEYS) {
    const v = ctx[key];
    if (typeof v === 'string' || typeof v === 'number') chips.push([key, String(v)]);
  }
  if (ctx.passwordChanged === true) chips.push(['passwordChanged', 'pw changed']);
  if (chips.length === 0) return null;
  return (
    <span className="inline-flex flex-wrap gap-1 ml-1">
      {chips.map(([key, value]) => (
        <span
          key={key}
          title={key}
          className={`text-xs rounded-sm px-1.5 py-0.5 ${chipClass(key, value)}`}
        >
          {value}
        </span>
      ))}
    </span>
  );
}

function ContextBlock({ ctx, searchNeedle }: { ctx: Record<string, unknown> | null; searchNeedle?: string }) {
  // null = user hasn't chosen; a ctx that matches the search opens by default.
  const [userChoice, setUserChoice] = useState<boolean | null>(null);
  if (!ctx) return null;
  const matchesSearch =
    !!searchNeedle && JSON.stringify(ctx).toLowerCase().includes(searchNeedle);
  const open = userChoice ?? matchesSearch;
  return (
    <Collapsible className="mt-1" open={open} onOpenChange={setUserChoice}>
      <CollapsibleTrigger render={<Button variant="link" size="sm" title={open ? 'Hide the extra context the app logged with this entry' : 'Show the extra context the app logged with this entry, as JSON'} />}>
        {open ? 'hide ctx' : 'show ctx'}
      </CollapsibleTrigger>
      <CollapsibleContent>
        <pre className="mt-1 text-xs bg-gray-50 border border-border rounded-sm p-2 whitespace-pre-wrap break-all">
          {JSON.stringify(ctx, null, 2)}
        </pre>
      </CollapsibleContent>
    </Collapsible>
  );
}

const AppLogsTimeline: React.FC<Props> = ({ entries, searchNeedle }) => {
  if (entries.length === 0) {
    return <div className="text-sm text-muted-foreground">No entries match the current filters.</div>;
  }
  return (
    <div className="border border-border rounded-sm divide-y">
      {entries.map((e) => (
        <div key={e.id} className="p-2 text-sm font-mono">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground whitespace-nowrap">{e.ts}</span>
            <span
              className={`text-xs font-semibold rounded-sm px-1.5 py-0.5 ${LEVEL_CLASS[e.level] ?? LEVEL_CLASS.info}`}
            >
              {e.level}
            </span>
            <span className="text-xs border border-status-neutral-border text-status-neutral-foreground rounded-sm bg-status-neutral px-1.5 py-0.5">{e.source}</span>
            <span className="text-xs text-gray-600">seq {e.seq}</span>
            <TaxonomyChips ctx={e.ctx} />
          </div>
          <div className="mt-1 whitespace-nowrap overflow-x-auto">{e.msg}</div>
          <ContextBlock ctx={e.ctx} searchNeedle={searchNeedle} />
        </div>
      ))}
    </div>
  );
};

export default AppLogsTimeline;
